import { FormActionFrameworkContext } from './context';
import { createStepFromJson, executeSteps } from './step';
import './registry';
import { FORM_MILESTONES, } from './types';
/**
 * Loads configured milestone actions and runs them in order for a given milestone.
 * Not attached to SurveyJS in this pass — call `execute` from future integration code.
 */
export class FormActionFramework {
    constructor() {
        Object.defineProperty(this, "formId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "milestoneActions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: emptyMilestones()
        });
        /**
         * Host/page identity set at init. Seeded onto every execute context.
         * Not mutated by steps.
         */
        Object.defineProperty(this, "hostContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        /**
         * Variables that persist across milestone executions for this FAF instance
         * (e.g. formLoad → formChange during a test session).
         */
        Object.defineProperty(this, "sessionVariables", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
    }
    /**
     * @param config Milestone/step JSON
     * @param host Optional page identity (formId, incidentId, taskId, pageUrl, …)
     */
    init(config, host) {
        this.fromJSON(config ?? { milestones: {} });
        if (host)
            this.setHostContext(host);
    }
    setHostContext(host) {
        this.hostContext = { ...(host ?? {}) };
        if (this.hostContext.pageUrl == null && typeof window !== 'undefined') {
            this.hostContext.pageUrl = window.location.href;
        }
        // Keep engine.formId aligned with host when provided.
        if (this.hostContext.formId != null && this.hostContext.formId !== '') {
            this.formId = this.hostContext.formId;
        }
    }
    fromJSON(config) {
        this.formId = config?.formId;
        this.milestoneActions = emptyMilestones();
        const milestones = config?.milestones ?? {};
        for (const milestone of FORM_MILESTONES) {
            const list = milestones[milestone] ?? [];
            this.milestoneActions[milestone] = list.map((json) => createStepFromJson(json));
        }
    }
    toJSON() {
        const milestones = {};
        for (const milestone of FORM_MILESTONES) {
            const steps = this.milestoneActions[milestone] ?? [];
            if (steps.length > 0) {
                milestones[milestone] = steps.map((step) => step.toJSON());
            }
        }
        return {
            formId: this.formId,
            milestones,
        };
    }
    getSteps(milestone) {
        return this.milestoneActions[milestone] ?? [];
    }
    setSteps(milestone, steps) {
        this.milestoneActions[milestone] = steps;
    }
    clearSessionVariables() {
        this.sessionVariables.clear();
    }
    /**
     * Build a context for a milestone run: host identity + caller-supplied form/data.
     * Prefer this over constructing FormActionFrameworkContext by hand.
     */
    createContext(partial) {
        const host = resolveHostContext(this.hostContext);
        return new FormActionFrameworkContext({
            ...partial,
            formId: host.formId ?? partial?.formId ?? null,
            incidentId: host.incidentId ?? partial?.incidentId ?? null,
            taskId: host.taskId ?? partial?.taskId ?? null,
            userId: host.userId ?? partial?.userId ?? null,
            orgId: host.orgId ?? partial?.orgId ?? null,
            pageUrl: host.pageUrl ?? partial?.pageUrl ?? null,
            organizationId: host.orgId != null
                ? String(host.orgId)
                : partial?.organizationId,
        });
    }
    async execute(context, milestone, options) {
        applyHostContext(context, this.hostContext);
        context.milestone = milestone;
        context.startTime = context.startTime ?? new Date();
        context.shouldStop = false;
        // Seed context with session variables (context values win on key clash).
        for (const [key, value] of this.sessionVariables.entries()) {
            if (!context.variables.has(key)) {
                context.variables.set(key, value);
            }
        }
        const steps = this.getSteps(milestone);
        context.log(`[FAF] milestone=${milestone} begin (${steps.length} steps)` +
            ` formId=${context.formId ?? '∅'} incidentId=${context.incidentId ?? '∅'}` +
            ` taskId=${context.taskId ?? '∅'}`);
        await executeSteps(steps, context, options);
        // Persist for later milestones / steps in this FAF instance.
        this.sessionVariables = new Map(context.variables);
        context.log(`[FAF] milestone=${milestone} end shouldStop=${context.shouldStop}`);
        return context;
    }
}
function resolveHostContext(host) {
    const pageUrl = host.pageUrl ??
        (typeof window !== 'undefined' ? window.location.href : null);
    return { ...host, pageUrl };
}
/** Overlay read-only host identity onto an execution context. */
function applyHostContext(context, host) {
    const resolved = resolveHostContext(host);
    context.formId = resolved.formId ?? context.formId ?? null;
    context.incidentId = resolved.incidentId ?? context.incidentId ?? null;
    context.taskId = resolved.taskId ?? context.taskId ?? null;
    context.userId = resolved.userId ?? context.userId ?? null;
    context.orgId = resolved.orgId ?? context.orgId ?? null;
    context.pageUrl = resolved.pageUrl ?? context.pageUrl ?? null;
    if (resolved.orgId != null) {
        context.organizationId = String(resolved.orgId);
    }
}
function emptyMilestones() {
    return {
        formLoad: [],
        formChange: [],
        formValidate: [],
        formSubmit: [],
        formPostSubmit: [],
    };
}
export { FormActionFrameworkContext };
